import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  filterBranchTree,
  formatParentNodeValue,
} from "./branchTreeData";
import {
  CurrentValueText,
  EmptyTreeText,
  HintText,
  SearchField,
  StyledDialog,
  StyledDialogContent,
  StyledDialogTitle,
  TreeBranchRoot,
  TreeIndent,
  TreeListItemButton,
  TreeRowOuter,
  TreeScrollBox,
  TreeSpacer,
} from "./SelectParentDialog.styles";

function TreeBranch({ node, depth, expanded, onToggle, onSelect }) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isOpen = expanded[node.id] ?? depth < 2;

  return (
    <TreeBranchRoot>
      <TreeRowOuter $depth={depth}>
        <TreeIndent>
          {hasChildren ? (
            <IconButton
              size="small"
              aria-label={isOpen ? "collapse" : "expand"}
              onClick={(e) => {
                e.stopPropagation();
                onToggle(node.id);
              }}
            >
              {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          ) : (
            <TreeSpacer />
          )}
        </TreeIndent>
        <TreeListItemButton onClick={() => onSelect(node)} dense>
          <ListItemText primary={formatParentNodeValue(node)} />
        </TreeListItemButton>
      </TreeRowOuter>
      {hasChildren ? (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children.map((child) => (
              <TreeBranch
                key={child.id}
                node={child}
                depth={depth + 1}
                expanded={expanded}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            ))}
          </List>
        </Collapse>
      ) : null}
    </TreeBranchRoot>
  );
}

TreeBranch.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    children: PropTypes.array,
  }).isRequired,
  depth: PropTypes.number.isRequired,
  expanded: PropTypes.objectOf(PropTypes.bool).isRequired,
  onToggle: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default function SelectParentDialog({
  open,
  onClose,
  onConfirm,
  initialValue,
  treeRoot,
  loading,
}) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const filteredRoot = useMemo(
    () => filterBranchTree(treeRoot, search),
    [treeRoot, search]
  );

  const handleToggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelect = (node) => {
    const numericId = Number(node?.nodeId);
    onConfirm({
      displayLabel: formatParentNodeValue(node),
      parentNodeId: Number.isFinite(numericId) ? numericId : null,
    });
    onClose();
  };

  const handleClose = () => {
    setSearch("");
    onClose();
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="select-parent-title"
      data-testid="select-parent-dialog"
    >
      <StyledDialogTitle id="select-parent-title">
        Select Parent
        <IconButton aria-label="close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent dividers>
        <SearchField
          fullWidth
          size="small"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ "data-testid": "select-parent-search" }}
        />
        {initialValue ? (
          <CurrentValueText variant="caption" display="block">
            Current: {initialValue}
          </CurrentValueText>
        ) : null}
        <TreeScrollBox>
          {loading ? (
            <EmptyTreeText>Loading parent nodes...</EmptyTreeText>
          ) : !treeRoot ? (
            <EmptyTreeText>No branch hierarchy is available for this node type.</EmptyTreeText>
          ) : !filteredRoot ? (
            <EmptyTreeText>No branches match your search.</EmptyTreeText>
          ) : (
            <List dense disablePadding>
              {filteredRoot.id === "context-root"
                ? filteredRoot.children.map((child) => (
                    <TreeBranch
                      key={child.id}
                      node={child}
                      depth={0}
                      expanded={expanded}
                      onToggle={handleToggle}
                      onSelect={handleSelect}
                    />
                  ))
                : (
                  <TreeBranch
                    node={filteredRoot}
                    depth={0}
                    expanded={expanded}
                    onToggle={handleToggle}
                    onSelect={handleSelect}
                  />
                )}
            </List>
          )}
        </TreeScrollBox>
        <HintText variant="caption" display="block">
          Select an existing branch node. New nodes cannot be added from this dialog.
        </HintText>
      </StyledDialogContent>
    </StyledDialog>
  );
}

SelectParentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  initialValue: PropTypes.string,
  treeRoot: PropTypes.shape({
    id: PropTypes.string,
    nodeId: PropTypes.number,
    name: PropTypes.string,
    children: PropTypes.array,
  }),
  loading: PropTypes.bool,
};

SelectParentDialog.defaultProps = {
  initialValue: "",
  treeRoot: null,
  loading: false,
};
